import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Phase 7c — a 1:1 port of the SHARED `DeletePopupView`
 * (`django/static/mvc/view/delete_popup.js` + `tmpl/delete_popup.tmpl`), as
 * instantiated by `openCompanyDeletePopup` (`partners.js:377-397`) and
 * `openContactDeletePopup` (`:356-376`). Discovery §4.10 / Q15 / Q16.
 *
 * ★ THE OUTER NODE IS THE VIEW'S `el`. `DeletePopupView` has
 * `tagName: "div"` and `className: "popup-wrapper width-small active"`
 * (`delete_popup.js:17-19`), and `render()` puts the compiled template INSIDE
 * it (`:44`). The opener then does `this.$el.append(popupView.$el)` where
 * `this.$el` is **`#ksdm-content`** (`partners.js:21`, `:373`, `:393`) — so the
 * popup is a LAST CHILD OF `#ksdm-content`, a sibling of `#partners`, not a
 * portal to `<body>`. `PartnersPage` renders it in exactly that position.
 * `active` is hardcoded in the className, never toggled: the view is created
 * already-open and destroyed on close.
 *
 * ★ Q16 — STACKING IS ALLOWED, AND THAT IS PARITY. Nothing in the legacy code
 * tracks whether a popup is already open, so two delete links open two
 * overlapping modals. There is NO backdrop element, NO focus trap, NO
 * `aria-modal`, NO `role="dialog"` and NO scroll lock — the page behind stays
 * fully interactive. None of that is added here.
 *
 * ★ `#btn_cancel` IS FOCUSED BY THE OPENER, NOT BY THIS VIEW, AND IT IS AN
 * ID LOOKUP. `partners.js:374`/`:394` run `$('#btn_cancel').focus()` straight
 * after the append — which is `getElementById` semantics, i.e. the FIRST
 * `#btn_cancel` in document order. With a popup already open, opening a second
 * one therefore focuses the FIRST popup's Cancel button, not the new one's.
 * Reproduced verbatim below (a document-wide lookup in a layout effect, not a
 * ref to this popup's own button) because it is observable: `Enter` after
 * opening a second popup cancels the first.
 *
 * ★ Q15 — ONE `Escape` CLOSES EVERY STACKED POPUP. `render()` adds a
 * `document` `keyup` listener per view and NEVER removes it
 * (`delete_popup.js:45-49`), so N opens leave N listeners, each calling its own
 * view's `cancelDelete()`. Pressing Escape once therefore closes ALL open
 * popups at once — verified live (`twoPopupsOpen:2` → `afterEscWithTwo:0`).
 * Reproduced by giving every mounted popup its own `document` listener: one
 * Escape still cancels all of them.
 *   DELIBERATE, UNOBSERVABLE DEVIATION: this listener IS removed on unmount,
 *   whereas legacy leaks it for the lifetime of the page. The leaked copies
 *   call `cancelDelete()` on an already-`remove()`d view, and Backbone's
 *   `remove()` is idempotent, so they do nothing — Q15 itself calls them
 *   "harmless". Keeping them would leak a listener per open with no
 *   behavioural payoff.
 * `e.which == 27` is legacy's test (`:46`) and is kept literally; `which` is
 * deprecated but present in every browser this app supports.
 *
 * ★ NO ENTER HANDLER, NO `autofocus`, NO BACKDROP CLICK. The only two ways to
 * close are the two buttons and Escape. Enter "works" solely because
 * `#btn_cancel` has focus and it is a `<button>` — do not add a keydown
 * handler.
 *
 * ★ THE CONFIRM PATH CAN LEAVE THE POPUP OPEN — ON PURPOSE (Q9).
 * `confirmDelete` (`delete_popup.js:52-60`) calls `remover_function()` and only
 * THEN `self.remove()`. When the remover throws — which is exactly what
 * deleting a contact found through a contact search does, because the searched
 * row carries no `data-name` and `elem.remove()` hits `null` — the exception
 * escapes before `self.remove()` and the popup stays on screen with the DELETE
 * already sent. `onConfirm` is therefore called inside a `try`, and a throw
 * leaves the popup mounted. See `PartnersPage.deleteContact` for the owner's
 * documented deviation (Escape/Cancel keep working).
 */
interface DeletePopupProps {
  /** `popup_main.title` — "Delete Partner?" / "Delete Contact?" (§4.10). */
  title: string;
  /** `popup_main.content` — the one-sentence body copy. */
  content: string;
  /**
   * `popup_main.name` → `li.model-name`. Company: `model.get('name')` verbatim
   * (which is `null` → Handlebars renders it as an EMPTY `<li>`). Contact:
   * `model.get('name') ? model.get('name') : 'Empty contact'` (`:365`).
   */
  name: string | null;
  /** `remover_function` (`:369`, `:389`). A throw keeps the popup open — Q9. */
  onConfirm: () => void;
  /** `cancelDelete` → `self.remove()` (`:62-65`). */
  onClose: () => void;
}

const DeletePopup = ({ title, content, name, onConfirm, onClose }: DeletePopupProps) => {
  // Latest-value refs so the document listener never has to be re-bound (and so
  // it behaves like legacy's, which closes over `self` once and for good).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  /* `$('#btn_cancel').focus()` — see the header comment. Layout effect, so the
     focus lands in the same frame the node was appended, as it does in legacy. */
  useLayoutEffect(() => {
    document.getElementById('btn_cancel')?.focus();
  }, []);

  /* `delete_popup.js:45-49` — Q15. */
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.which === 27) onCloseRef.current();
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, []);

  /* `confirmDelete` (`delete_popup.js:52-60`). The `catch` is the Q9 boundary:
     legacy's exception aborts the function before `self.remove()`, so the popup
     survives; here the same throw returns early and the popup stays mounted.
     Nothing is logged or shown — legacy shows nothing either. */
  const handleConfirm = () => {
    try {
      onConfirmRef.current();
    } catch {
      return;
    }
    onCloseRef.current();
  };

  return (
    <div className="popup-wrapper width-small active">
      <div className="popup-content delete-popup">
        <h2 className="popup-heading">{title}</h2>
        <div className="popup-content-part">
          <p>{content}</p>
          <ul className="deleted-items">
            {/* Handlebars `{{name}}` — a null renders as the empty string, i.e.
                the `<li>` is present but empty (a company whose `name` column is
                NULL really does get a blank line here). */}
            <li className="model-name">{name ?? ''}</li>
          </ul>
        </div>
        <div className="popup-control-buttons">
          <button
            className="btn right not-highlighted-btn"
            name="confirm_delete"
            onClick={handleConfirm}
          >
            Delete
          </button>
          <button
            className="btn right btn_cancel highlighted-btn"
            id="btn_cancel"
            name="cancel_delete"
            onClick={() => onCloseRef.current()}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeletePopup;
